<template>
	<EventLayout :event="event" :subtitle="trans('events.bank_accounts.title')">
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="showModal('create-bank-account-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Modal id="create-bank-account-modal">
			<template #title>{{ trans('events.bank_accounts.create') }}</template>
			<div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
				<LabeledObject full required class="lg:col-span-2">
					<template #label>{{ trans('events.bank_accounts.type') }}</template>
					<select v-model="newBankAccount.type">
						<option value="checking">{{ trans('enums.bank_account.checking') }}</option>
						<option value="savings">{{ trans('enums.bank_account.savings') }}</option>
						<option value="money-market">{{ trans('enums.bank_account.money-market') }}</option>
						<option value="certificate-of-deposit">{{ trans('enums.bank_account.certificate-of-deposit') }}</option>
					</select>
				</LabeledObject>
				<LabeledObject full required>
					<template #label>{{ trans('events.bank_accounts.bank') }}</template>
					<IconTextInput v-model="newBankAccount.bank" icon-class="fa-solid fa-bank" full/>
				</LabeledObject>
				<LabeledObject full required>
					<template #label>{{ trans('events.bank_accounts.owner') }}</template>
					<IconTextInput v-model="newBankAccount.owner" icon-class="fa-solid fa-user" full/>
				</LabeledObject>
				<LabeledObject full required>
					<template #label>{{ trans('events.bank_accounts.number') }}</template>
					<IconTextInput v-model="newBankAccount.number" icon-class="fa-solid fa-hashtag" full/>
				</LabeledObject>
				<LabeledObject full required>
					<template #label>{{ trans('events.bank_accounts.alias') }}</template>
					<IconTextInput v-model="newBankAccount.alias" icon-class="fa-solid fa-tag" full/>
				</LabeledObject>
			</div>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="insert">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<Empty v-if="bankAccounts.length === 0"/>
		<CardGrid v-else tight>
			<template v-for="bankAccount in bankAccounts" :key="`payment-service-${bankAccount.id}`">
				<Modal :id="`edit-bank-account-${bankAccount.id}-modal`">
					<template #title>{{ trans('events.bank_accounts.edit') }}</template>
					<div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
						<LabeledObject full required class="lg:col-span-2">
							<template #label>{{ trans('events.bank_accounts.type') }}</template>
							<select v-model="bankAccount.type">
								<option value="checking">{{ trans('enums.bank_account.checking') }}</option>
								<option value="savings">{{ trans('enums.bank_account.savings') }}</option>
								<option value="money-market">{{ trans('enums.bank_account.money-market') }}</option>
								<option value="certificate-of-deposit">{{ trans('enums.bank_account.certificate-of-deposit') }}</option>
							</select>
						</LabeledObject>
						<LabeledObject full required>
							<template #label>{{ trans('events.bank_accounts.bank') }}</template>
							<IconTextInput v-model="bankAccount.bank" icon-class="fa-solid fa-bank" full/>
						</LabeledObject>
						<LabeledObject full required>
							<template #label>{{ trans('events.bank_accounts.owner') }}</template>
							<IconTextInput v-model="bankAccount.owner" icon-class="fa-solid fa-user" full/>
						</LabeledObject>
						<LabeledObject full required>
							<template #label>{{ trans('events.bank_accounts.number') }}</template>
							<IconTextInput v-model="bankAccount.number" icon-class="fa-solid fa-hashtag" full/>
						</LabeledObject>
						<LabeledObject full required>
							<template #label>{{ trans('events.bank_accounts.alias') }}</template>
							<IconTextInput v-model="bankAccount.alias" icon-class="fa-solid fa-tag" full/>
						</LabeledObject>
					</div>
					<template #footer>
						<PrimaryButton :color="Color.Green" @click.stop="update(bankAccount)">
							<i class="fa-solid fa-check"/>
						</PrimaryButton>
					</template>
				</Modal>
				<Card :title="bankAccount.bank">
					<template #badges>
						<Badge active>{{ trans(`enums.bank_account.${bankAccount.type}`) }}</Badge>
					</template>
					<LabeledObject>
						<template #label>{{ trans('events.bank_accounts.owner') }}</template>
						<p>{{ bankAccount.owner }}</p>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.bank_accounts.number') }}</template>
						<p>{{ bankAccount.number }}</p>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.bank_accounts.alias') }}</template>
						<p>{{ bankAccount.alias }}</p>
					</LabeledObject>
					<template #footer>
						<SecondaryButton :color="Color.Blue" @click.stop="showModal(`edit-bank-account-${bankAccount.id}-modal`)">
							<i class="fa-solid fa-edit mr-2"/>
							{{ trans('general.edit') }}
						</SecondaryButton>
						<SecondaryButton :color="Color.Red" @click.stop="remove(bankAccount)">
							<i class="fa-solid fa-trash mr-2"/>
							{{ trans('general.delete') }}
						</SecondaryButton>
					</template>
				</Card>
			</template>
		</CardGrid>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import Card from '@/Components/UI/Cards/Card.vue'
import {defineProps, type Ref, ref} from 'vue'
import {type Event} from '@/Types/Events/Event'
import Modal from '@/Components/UI/Modals/Modal.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import EventLayout from '@/Layouts/EventLayout.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import IconTextInput from '@/Components/UI/Inputs/IconTextInput.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type BankAccount} from '@/Types/Events/Payments/BankAccount'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	event: Event,
	bankAccounts: Array<BankAccount>,
}>()

const event: Ref<Event> = ref(props.event)
const working: Ref<boolean> = ref(false)
const bankAccounts: Ref<Array<BankAccount>> = ref(props.bankAccounts)
const newBankAccount: Ref<BankAccount> = ref({
	owner: '',
	bank: '',
	number: '',
	alias: '',
	type: 'checking',
} as BankAccount)

function insert(): void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('events.payments.bank-accounts.store', event.value.id), newBankAccount.value).then(({data}) => {
			showSuccess(trans('general.inserted'))
			newBankAccount.value.bank = ''
			newBankAccount.value.owner = ''
			newBankAccount.value.number = ''
			newBankAccount.value.alias = ''
			newBankAccount.value.type = 'checking'
			bankAccounts.value.unshift(data)
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function update(bankAccount: BankAccount): void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.payments.bank-accounts.update', {event: event.value.id, bankAccount: bankAccount.id}), bankAccount).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function remove(bankAccount: BankAccount): void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('events.payments.bank-accounts.delete', {event: event.value.id, bankAccount: bankAccount.id})).then(() => {
			showSuccess(trans('general.deleted'))
			const index = bankAccounts.value.findIndex((b: BankAccount) => b.id === bankAccount.id)
			if(index > -1){
				bankAccounts.value.splice(index, 1)
			}
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>